/**
 * CategoryPopup Component - Production Optimized Version
 * 
 * Key Improvements:
 * - Better portal positioning with dynamic calculations
 * - Improved accessibility (ARIA attributes, keyboard navigation)
 * - Request cancellation for API calls
 * - Cleaner navigation logic
 * - Better error handling and loading states
 * - Reduced re-renders with memoization
 * - Mobile click-outside detection
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, X, Package, Loader } from 'lucide-react';
import { buildHierarchy } from '../utils/buildHierarchy';
import { debugLog } from '../constants/config';

const WP_API_BASE = process.env.REACT_APP_WP_BASE || 'https://goldensparrowuae.com/wordpress/wp-json';

const POPUP_STYLES = `
  .collapse-wrap {
    overflow: hidden;
    transition: max-height 240ms ease-in-out;
  }
  .chev {
    transition: transform 180ms ease;
  }
  .chev-open {
    transform: rotate(90deg);
  }
`;


const HIDDEN_CATEGORY_SLUGS = ['forklifts'];

const removeHiddenCategories = (nodes) =>
  nodes
    .filter((node) => !HIDDEN_CATEGORY_SLUGS.includes(node.slug))
    .map((node) => ({ ...node, children: removeHiddenCategories(node.children) }));


export default function CategoryPopup({ 
  isOpen, 
  onClose, 
  onSelectCategory, 
  onMouseEnter, 
  onMouseLeave,
  triggerRef
}) {
  const [activeTab, setActiveTab] = useState('sales');
  const [salesCategories, setSalesCategories] = useState([]);
  const [rentalCategories, setRentalCategories] = useState([]);
  const [loadingSales, setLoadingSales] = useState(false);
  const [loadingRentals, setLoadingRentals] = useState(false);
  const [error, setError] = useState(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  
  // ✅ NEW: Ref for the popup content to detect clicks outside
  const popupRef = useRef(null);
  
  const navigate = useNavigate();

  // ✅ Calculate popup position dynamically
  useEffect(() => {
    const calculatePosition = () => {
      if (isOpen && triggerRef?.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const scrollY = window.scrollY || window.pageYOffset;
        
        setPosition({
          top: rect.bottom + scrollY + 8,
          left: 0,
          width: window.innerWidth
        });
      }
    };

    calculatePosition();
    
    if (isOpen) {
      window.addEventListener('resize', calculatePosition);
      window.addEventListener('scroll', calculatePosition, { passive: true });
      
      return () => {
        window.removeEventListener('resize', calculatePosition);
        window.removeEventListener('scroll', calculatePosition);
      };
    }
  }, [isOpen, triggerRef]);

  // ✅ NEW: Handle click outside on mobile devices
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      // Check if click is outside the popup AND outside the trigger button
      if (
        popupRef.current && 
        !popupRef.current.contains(event.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(event.target)
      ) {
        onClose?.();
      }
    };

    // Use a small delay to prevent immediate closing on popup open
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose, triggerRef]);

  // ✅ Fetch sales categories with abort controller
  useEffect(() => {
    if (!isOpen || salesCategories.length > 0) return;

    const controller = new AbortController();

    const fetchSalesCategories = async () => {
      setLoadingSales(true);
      setError(null);
      try {
        const response = await fetch(
          `${WP_API_BASE}/wp/v2/equipment-buy?per_page=100`,
          { signal: controller.signal }
        );
        
        if (!response.ok) throw new Error('Failed to fetch sales categories');
        
        const data = await response.json();
        const hierarchy = removeHiddenCategories(buildHierarchy(data));
        setSalesCategories(hierarchy);
      } catch (err) {
        if (err.name !== 'AbortError') {
          debugLog('Error fetching sales categories:', err);
          setError('Failed to load sales categories');
        }
      } finally {
        setLoadingSales(false);
      }
    };

    fetchSalesCategories();

    return () => controller.abort();
  }, [isOpen, salesCategories.length]);

  // ✅ Fetch rental categories with abort controller
  useEffect(() => {
    if (!isOpen || activeTab !== 'rentals' || rentalCategories.length > 0) return;

    const controller = new AbortController();

    const fetchRentalCategories = async () => {
      setLoadingRentals(true);
      setError(null);
      try {
        const response = await fetch(
          `${WP_API_BASE}/wp/v2/equipment-rent?per_page=100`,
          { signal: controller.signal }
        );
        
        if (!response.ok) throw new Error('Failed to fetch rental categories');
        
        const data = await response.json();
        const hierarchy = removeHiddenCategories(buildHierarchy(data));
        setRentalCategories(hierarchy);
      } catch (err) {
        if (err.name !== 'AbortError') {
          debugLog('Error fetching rental categories:', err);
          setError('Failed to load rental categories');
        }
      } finally {
        setLoadingRentals(false);
      }
    };

    fetchRentalCategories();

    return () => controller.abort();
  }, [isOpen, activeTab, rentalCategories.length]);

  // ✅ Memoized current category tree
  const currentTree = useMemo(
    () => (activeTab === 'sales' ? salesCategories : rentalCategories),
    [activeTab, salesCategories, rentalCategories]
  );
  const currentLoading = activeTab === 'sales' ? loadingSales : loadingRentals;
  // ✅ Handle category selection with clean navigation
  const handleSelect = useCallback((node) => {
    const taxonomy = activeTab === 'sales' ? 'equipment-buy' : 'equipment-rent';
    
    if (onSelectCategory) {
      onSelectCategory({
        taxonomy,
        termId: node.id,
        termSlug: node.slug,
        name: node.name,
      });
    }
    
    navigate(
      `/products?taxonomy=${taxonomy}&term=${node.slug}&termName=${encodeURIComponent(node.name)}`,
      { replace: true }
    );
    
    onClose?.();
  }, [activeTab, navigate, onSelectCategory, onClose]);

  // ✅ Retry handler
  const handleRetry = useCallback(() => {
    if (activeTab === 'sales') {
      setSalesCategories([]);
    } else {
      setRentalCategories([]);
    }
  }, [activeTab]);

  // ✅ Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // ✅ Portal content
  const popupContent = (
    <div 
      className="fixed pointer-events-none z-[100] w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8"
      style={{
        top: `${position.top}px`,
        left: 0,
        right: 0
      }}
      onMouseEnter={onMouseEnter} 
      onMouseLeave={onMouseLeave}
      role="dialog"
      aria-modal="true"
      aria-labelledby="category-popup-title"
    >
      <style>{POPUP_STYLES}</style>

      <div 
        ref={popupRef}
        className="bg-white w-full max-w-5xl rounded-xl shadow-2xl overflow-hidden pointer-events-auto"
        style={{ 
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' 
        }}
      >
        {/* Header */}
        <div className="bg-[#1A1A1A] text-white px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <h2 
              id="category-popup-title"
              className="text-lg sm:text-2xl font-bold font-display"
            >
              Browse All Categories
            </h2>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white rounded"
              aria-label="Close category browser"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-neutral-50 border-b" role="tablist">
          <div className="flex">
            <button
              role="tab"
              aria-selected={activeTab === 'sales'}
              aria-controls="sales-panel"
              id="sales-tab"
              className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-semibold transition-colors relative focus:outline-none ${
                activeTab === 'sales'
                  ? 'bg-white text-black border-b-2 border-black'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
              onClick={() => setActiveTab('sales')}
            >
              <span className="flex items-center justify-center gap-2">
                Equipment for Sale
              </span>
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'rentals'}
              aria-controls="rentals-panel"
              id="rentals-tab"
              className={`flex-1 px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-semibold transition-colors relative focus:outline-none ${
                activeTab === 'rentals'
                  ? 'bg-white text-black border-b-2 border-black'
                  : 'text-neutral-600 hover:text-neutral-900'
              }`}
              onClick={() => setActiveTab('rentals')}
            >
              <span className="flex items-center justify-center gap-2">
                Equipment for Rent
              </span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div 
          role="tabpanel"
          id={`${activeTab}-panel`}
          aria-labelledby={`${activeTab}-tab`}
          className="p-4 sm:p-6 max-h-[60vh] sm:max-h-[70vh] overflow-y-auto"
        >
          {error ? (
            <div className="text-red-600 text-center py-8">
              <p className="text-sm sm:text-base mb-2">{error}</p>
              <button 
                onClick={handleRetry}
                className="mt-4 px-4 py-2 text-sm sm:text-base bg-gradient-to-br from-[#FFD200] to-[#F7971E] text-white rounded-xl hover:from-[#F7971E] hover:to-[#FFD200] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F7971E]"
              >
                Retry
              </button>
            </div>
          ) : currentLoading ? (
            <div className="flex items-center justify-center py-12" role="status">
              <Loader className="w-6 h-6 sm:w-8 sm:h-8 animate-spin text-black" aria-hidden="true" />
              <span className="ml-3 text-sm sm:text-base text-neutral-600">Loading categories...</span>
            </div>
          ) : currentTree.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <Package className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-neutral-300" aria-hidden="true" />
              <p className="text-sm sm:text-base">No categories found</p>
            </div>
          ) : (
            <CategoryTree
              categories={currentTree}
              onSelect={handleSelect}
              defaultOpenDepth={0}
            />
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
}

/* ========== Category Tree Component ========== */

const CategoryTree = React.memo(({ categories, onSelect, defaultOpenDepth = 0 }) => {
  return (
    <ul className="space-y-1 sm:space-y-2" role="tree">
      {categories.map((cat) => (
        <CategoryNode
          key={cat.id}
          category={cat}
          depth={0}
          defaultOpenDepth={defaultOpenDepth}
          onSelect={onSelect}
        />
      ))}
    </ul>
  );
});

CategoryTree.displayName = 'CategoryTree';

/* ========== Category Node Component ========== */

const CategoryNode = React.memo(({ category, depth, defaultOpenDepth, onSelect }) => {
  const [isOpen, setIsOpen] = useState(depth <= defaultOpenDepth);
  const hasChildren = Array.isArray(category.children) && category.children.length > 0;
  
  const toggle = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasChildren) setIsOpen((v) => !v);
  }, [hasChildren]);

  const handleClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect?.(category);
  }, [category, onSelect]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onSelect?.(category);
    } else if (e.key === 'ArrowRight' && hasChildren && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    } else if (e.key === 'ArrowLeft' && hasChildren && isOpen) {
      e.preventDefault();
      setIsOpen(false);
    }
  }, [category, onSelect, hasChildren, isOpen]);

  const maxH = isOpen ? '999px' : '0px';

  return (
    <li role="treeitem" aria-expanded={hasChildren ? isOpen : undefined}>
      <div 
        className={`flex items-center py-1.5 sm:py-2 px-2 sm:px-3 rounded-lg sm:rounded-xl hover:bg-neutral-50 transition-colors ${
          depth === 0 ? 'font-semibold' : ''
        }`}
      >
        {hasChildren ? (
          <button
            type="button"
            className={`chev mr-1 sm:mr-2 text-neutral-400 hover:text-neutral-600 p-0.5 sm:p-1 focus:outline-none ${
              isOpen ? 'chev-open' : ''
            }`}
            onClick={toggle}
            aria-label={isOpen ? `Collapse ${category.name}` : `Expand ${category.name}`}
          >
            <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        ) : (
          <span className="w-5 sm:w-7 mr-1 sm:mr-2" aria-hidden="true" />
        )}

        <button
          type="button"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          className="flex-1 text-left text-sm sm:text-base hover:text-gray-900 transition-colors cursor-pointer focus:outline-none px-1"
          title={`View ${category.name} products`}
        >
          <span>{category.name}</span>
          {category.count > 0 && (
            <span className="ml-2 text-xs text-neutral-500" aria-label={`${category.count} products`}>
              ({category.count})
            </span>
          )}
        </button>
      </div>

      {hasChildren && (
        <div
          className="ml-4 sm:ml-6 border-l-2 border-neutral-100 collapse-wrap"
          style={{ maxHeight: maxH }}
          role="group"
        >
          <ul className="pl-2 sm:pl-4 mt-1 space-y-1" role="group">
            {category.children.map((child) => (
              <CategoryNode
                key={child.id}
                category={child}
                depth={depth + 1}
                defaultOpenDepth={defaultOpenDepth}
                onSelect={onSelect}
              />
            ))}
          </ul>
        </div>
      )}
    </li>
  );
});

CategoryNode.displayName = 'CategoryNode';